import { useQuery } from "@tanstack/react-query";
import apiClient from "@/app/api/client";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import { auditApi } from "../services/audit.api";
import type { AuditEntry, AuditFilterState } from "../types";

/**
 * ⚠️ Bo'sh qiymatlar `params` ga QO'SHILMAYDI — `usePaginatedQuery`
 * `params` ni queryKey ga kiritadi va `undefined` maydonlar keraksiz
 * qayta so'rovlarga olib kelardi.
 *
 * ⚠️ **`resourceId`** — `GET /audit` filtri (`audit.schemas.ts`
 * `AuditListQuerySchema.resourceId`). Bo'lim profilining "So'nggi
 * o'zgarishlar" bloki (`DepartmentOverviewTab`) buni `resourceType` bilan
 * BIRGA yuboradi — usiz so'rov shu turdagi BARCHA yozuvlarni qaytarardi,
 * aynan shu bo'limnikini emas.
 */
export function buildAuditListParams(filters: AuditFilterState): Record<string, string> {
	const params: Record<string, string> = {};

	if (filters.actorUserId) {
		params.actorUserId = filters.actorUserId;
	}
	if (filters.resourceType) {
		params.resourceType = filters.resourceType;
	}
	if (filters.resourceId) {
		params.resourceId = filters.resourceId;
	}
	if (filters.action) {
		params.action = filters.action;
	}
	if (filters.dateFrom) {
		params.dateFrom = filters.dateFrom;
	}
	if (filters.dateTo) {
		params.dateTo = filters.dateTo;
	}

	return params;
}

/** Audit ro'yxati. */
export function useAuditList(filters: AuditFilterState) {
	return usePaginatedQuery<AuditEntry>({
		queryKey: ["audit"],
		url: "/audit",
		params: buildAuditListParams(filters),
	});
}

/**
 * Bitta yozuvning tafsiloti — `before`/`after` va tayyor `diff`.
 *
 * ⚠️ Drawer yopiq bo'lganda so'rov yuborilmaydi (`enabled`), va yozuv
 * O'ZGARMAYDI (audit jurnali append-only) — shuning uchun kesh abadiy
 * yangi hisoblanadi.
 */
export function useAuditEntry(id: string | null) {
	return useQuery({
		queryKey: ["audit", "entry", id],
		queryFn: () => auditApi.get(id as string),
		enabled: id !== null,
		staleTime: Number.POSITIVE_INFINITY,
	});
}

interface ActorOption {
	userId: string;
	fullName: string;
}

/**
 * Aktyor filtri opsiyalari.
 *
 * ⚠️⚠️ **Qiymat — `userId`, `employees.id` EMAS.** `audit_log.actor_user_id`
 * foydalanuvchiga ishora qiladi; xodim id si uzatilsa filtr jimgina nol
 * qator qaytarardi.
 *
 * ⚠️ Xodim profili yo'q aktyor (masalan sof administrator) ro'yxatda
 * ko'rinmaydi — bu backend ning `actorName` ni `employees` dan olishi
 * bilan bir xil cheklov. Bunday yozuvlarni `actorUserId` filtrisiz ham
 * ko'rish mumkin.
 *
 * ⚠️ `retry: false` — ro'yxat `employees:read` ruxsatini talab qiladi;
 * 403 kelganda filtr jimgina bo'sh qoladi va sahifa ishlashda davom etadi.
 */
export function useAuditActorOptions(search: string) {
	return useQuery({
		queryKey: ["audit", "actor-options", search],
		queryFn: async () => {
			const params: Record<string, string | number> = { page: 1, limit: 100 };

			if (search.trim()) {
				params.q = search.trim();
			}

			const response = await apiClient.get<{
				success: true;
				data: { items: ActorOption[] };
			}>("/employees", { params });

			return response.data.data.items;
		},
		staleTime: 30_000,
		retry: false,
	});
}
